import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import XIcon from "../../assets/icons/x.svg?react";
import { SubmitHandler, useForm } from "react-hook-form";
import { GetPreferenceResponse } from "../../types/api/api-types";
import { useEffect, useState } from "react";
import StyledSpinner from "../styled/StyledSpinner";
import { OtherFeesData, OtherFeesSchema } from "../../types/forms/preferences/otherFees.form";
import StyledInput from "../styled/StyledInput";
import { usePatchPreferenceOtherFees } from "../../api/preference.api";

type OtherFeesModalProps = {
  closeModal: () => void;
  preference: GetPreferenceResponse;
};

export const OtherFeesModal = ({ closeModal, preference }: OtherFeesModalProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: patchPreferenceOtherFees } = usePatchPreferenceOtherFees();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const otherFeesForm = useForm<OtherFeesData>({
    resolver: zodResolver(OtherFeesSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      otherEuFees: {
        otherFeesRange: preference.otherEuFees.otherFeesRange,
      },
      otherNonEuFees: {
        otherFeesRange: preference.otherNonEuFees.otherFeesRange,
      },
      otherUsaFees: {
        otherFeesRange: preference.otherUsaFees.otherFeesRange,
      },
    },
  });

  const onSubmitUserForm: SubmitHandler<OtherFeesData> = async (data) => {
    setIsLoading(true);
    try {
      const ret = await patchPreferenceOtherFees(data);
      setIsLoading(false);
      if (ret?.status === 200) {
        closeModal();
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  return (
    <dialog className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center overflow-hidden bg-[#00000080] p-8 backdrop-blur-sm">
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-lg bg-white">
        <div className="flex w-full flex-row items-center justify-between border-b border-gray-200 p-6">
          <div />
          <h2 className="text-center text-2xl font-semibold text-gray-900">
            {t("profileOtherFeesModal.title")}
          </h2>
          <button
            onClick={closeModal}
            className="h-fit w-fit rounded-full font-semibold hover:bg-gray-200"
          >
            <XIcon />
          </button>
        </div>
        <form
          onSubmit={otherFeesForm.handleSubmit(onSubmitUserForm)}
          className="flex w-full flex-col gap-4 overflow-hidden py-6"
        >
          <p className="w-full px-2 text-sm md:text-center md:text-base">
            {t("profileOtherFeesModal.description")}
          </p>
          <table className="block w-full overflow-x-auto">
            <thead>
              <tr className="border-t border-b border-gray-200 bg-gray-50">
                <th className="sticky left-0 z-20 bg-gray-50 p-3 text-sm font-medium text-gray-600">
                  {t("profileOtherFeesModal.zoneQuantityHeader")}
                </th>
                {preference.otherEuFees.otherFeesRange.map(
                  (fee: { amount: number; quantity: number }, index: number) => (
                    <th className="p-3 text-sm font-medium text-gray-600" key={index}>
                      {fee.quantity}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="sticky left-0 z-20 border-r border-gray-200 bg-white text-center">
                  {t("profileOtherFeesModal.euZone")}
                </td>
                {preference.otherEuFees.otherFeesRange.map(
                  (_fee: { amount: number; quantity: number }, index: number) => (
                    <td key={index} className="min-w-[100px] p-2 text-center">
                      <StyledInput
                        title=""
                        control={otherFeesForm.control}
                        name={`otherEuFees.otherFeesRange.${index}.amount`}
                        type="number"
                        className="w-full"
                        rightElement={<p>€</p>}
                      />
                    </td>
                  ),
                )}
              </tr>
              <tr className="border-b border-gray-200 shadow-xs">
                <td className="sticky left-0 z-20 border-r border-gray-200 bg-white text-center">
                  {t("profileOtherFeesModal.nonEuZone")}
                </td>
                {preference.otherNonEuFees.otherFeesRange.map(
                  (_fee: { amount: number; quantity: number }, index: number) => (
                    <td key={index} className="min-w-[100px] p-2 text-center">
                      <StyledInput
                        title=""
                        control={otherFeesForm.control}
                        name={`otherNonEuFees.otherFeesRange.${index}.amount`}
                        type="number"
                        className="w-full"
                        rightElement={<p>€</p>}
                      />
                    </td>
                  ),
                )}
              </tr>
              <tr className="border-b border-gray-200 shadow-xs">
                <td className="sticky left-0 z-20 border-r border-gray-200 bg-white text-center">
                  {t("profileOtherFeesModal.usaZone")}
                </td>
                {preference.otherNonEuFees.otherFeesRange.map(
                  (_fee: { amount: number; quantity: number }, index: number) => (
                    <td key={index} className="min-w-[100px] p-2 text-center">
                      <StyledInput
                        title=""
                        control={otherFeesForm.control}
                        name={`otherUsaFees.otherFeesRange.${index}.amount`}
                        type="number"
                        className="w-full"
                        rightElement={<p>€</p>}
                      />
                    </td>
                  ),
                )}
              </tr>
            </tbody>
          </table>
          <div className="flex w-full flex-col-reverse items-center gap-4 md:flex-row md:justify-center">
            <button
              type="button"
              className="h-fit w-full max-w-[300px] flex-1 rounded-lg border border-gray-200 bg-white px-4 py-3 text-base font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-800"
              onClick={closeModal}
            >
              {t("common:actions.back")}
            </button>
            <button
              // type="button"
              disabled={isLoading}
              onClick={() => {}}
              className="bg-brand700 hover:bg-brand600 h-fit w-full max-w-[300px] flex-1 rounded-lg px-4 py-3 text-base font-semibold text-white disabled:bg-gray-200"
            >
              {isLoading ? (
                <StyledSpinner width="20" height="20" />
              ) : (
                t("profileOtherFeesModal.submit")
              )}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
};
